import type { UpdateProjectDto } from '@/types/project.types';
import { Check, Pencil, X } from 'lucide-react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import type { FC } from 'react';
import { useState } from 'react';

interface ProjectTitleEditorProps {
  title: string;
  onSave: (updates: UpdateProjectDto) => Promise<void>;
}

export const ProjectTitleEditor: FC<ProjectTitleEditorProps> = ({ title, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (editedTitle.trim() === '') {
      return;
    }

    if (editedTitle === title) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave({ title: editedTitle });
      setIsEditing(false);
    } catch (_error) {
      // Error is handled by the hook
      setEditedTitle(title); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedTitle(title);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-4">
        <InputText
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          className="flex-1"
          disabled={isSaving}
          autoFocus={true}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSave();
            } else if (e.key === 'Escape') {
              handleCancel();
            }
          }}
        />
        <Button
          icon={<Check size={16} />}
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || editedTitle.trim() === ''}
          title="Save"
        />
        <Button icon={<X size={16} />} onClick={handleCancel} outlined={true} disabled={isSaving} title="Cancel" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      <Button icon={<Pencil size={16} />} onClick={() => setIsEditing(true)} text={true} title="Edit title" />
    </div>
  );
};
